/**
 * DeskView — the shell the Desk's views share (FEAT-087 / S-364).
 *
 * The redesign merges the old Today and Board screens; the approved comp
 * puts both behind an in-screen five-way selector (Queue · Board · Week ·
 * 4 weeks · Month) under a single Desk nav row.
 *
 * **Founder change, 2026-09-05:** Queue and Board became peer sidebar
 * entries instead, because switching between two primary ways of seeing
 * all your work reads as navigation, not as a control inside one of the
 * screens being switched. With both in the nav, a two-tab selector here
 * would be a second control for a choice the nav already makes — the same
 * duplication we removed from the workspace pickers — so this shell now
 * carries only what is common to every Desk view.
 *
 * Views stay routes (`/desk`, `/desk/board`, …) so they can be linked to
 * and the sidebar has something to match on. Where the three calendar
 * views land is still open — see the note in the FEAT-087 spec.
 */

import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { useState } from "react";
import { CreatePieceModal } from "@/components/CreatePieceModal";
import { useWorkspaceContext } from "@/lib/workspace-context";

export interface DeskContext {
  openCreatePiece: () => void;
}

/** The Desk shell's create-piece opener, for the view rendered inside it. */
export function useDeskContext(): DeskContext {
  return useOutletContext<DeskContext>();
}

export function DeskView() {
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspaceContext();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <main className="main desk">
      {/* The "+ New piece" button used to sit in a fixed bar above the view,
          while the Calendar carried its own inside the page header, scrolling
          with the content. Two screens, two behaviours, for one button. The
          shell now hands the opener down and each view puts it in its own
          header beside its controls, so all three read the same. */}
      <Outlet context={{ openCreatePiece: () => setShowCreate(true) } satisfies DeskContext} />

      {showCreate && activeWorkspace && (
        <CreatePieceModal
          agencyclientId={activeWorkspace.id}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/piece/${id}`);
          }}
        />
      )}
    </main>
  );
}
